import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';

type TeachingAssignmentStatus = 'Active' | 'Paused' | 'Ended';
type DeliveryType = 'OneToOne' | 'ClassroomSupport' | 'Group';

interface TeachingAssignment {
  id: number;
  organizationId: number;
  teacherUserId: number;
  teacherDisplayName: string;
  studentUserId: number;
  studentDisplayName: string;
  courseId: number;
  courseName: string;
  deliveryType: DeliveryType;
  status: TeachingAssignmentStatus;
  isPrimary: boolean;
  startDate: string;
  endDate?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UserOption {
  userId: number;
  displayName: string;
  email: string;
}

interface CourseOption {
  id: number;
  name: string;
  pathLabel: string;
}

interface LookupResponse {
  teachers: UserOption[];
  students: UserOption[];
  courses: CourseOption[];
}

interface FormState {
  teacherUserId: string;
  studentUserId: string;
  courseId: string;
  deliveryType: DeliveryType;
  status: TeachingAssignmentStatus;
  isPrimary: boolean;
  startDate: string;
  endDate: string;
  notes: string;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const emptyForm: FormState = {
  teacherUserId: '',
  studentUserId: '',
  courseId: '',
  deliveryType: 'OneToOne',
  status: 'Active',
  isPrimary: true,
  startDate: '',
  endDate: '',
  notes: '',
};

const toDateInput = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString();
};

const mapAssignmentToForm = (assignment: TeachingAssignment): FormState => ({
  teacherUserId: String(assignment.teacherUserId),
  studentUserId: String(assignment.studentUserId),
  courseId: String(assignment.courseId),
  deliveryType: assignment.deliveryType,
  status: assignment.status,
  isPrimary: assignment.isPrimary,
  startDate: toDateInput(assignment.startDate),
  endDate: toDateInput(assignment.endDate),
  notes: assignment.notes || '',
});

const ManageTeachingAssignments: React.FC = () => {
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<TeachingAssignment[]>([]);
  const [teachers, setTeachers] = useState<UserOption[]>([]);
  const [students, setStudents] = useState<UserOption[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [mode, setMode] = useState<'view' | 'create' | 'edit'>('view');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [studentFilter, setStudentFilter] = useState('');

  const requestedTeacherUserId = useMemo(() => {
    const value = Number(searchParams.get('teacherUserId'));
    return Number.isFinite(value) && value > 0 ? String(value) : '';
  }, [searchParams]);

  const requestedStudentUserId = useMemo(() => {
    const value = Number(searchParams.get('studentUserId'));
    return Number.isFinite(value) && value > 0 ? String(value) : '';
  }, [searchParams]);

  const selectedAssignment = useMemo(
    () => assignments.find((item) => item.id === selectedAssignmentId) || null,
    [assignments, selectedAssignmentId]
  );

  const loadData = useCallback(async () => {
    if (!hasActiveOrganization) {
      setError('No organization selected. Please switch organization first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      const [assignmentResponse, lookupResponse] = await Promise.all([
        axios.get<TeachingAssignment[]>(`${API_BASE_URL}/teachingassignments`, { headers }),
        axios.get<LookupResponse>(`${API_BASE_URL}/teachingassignments/lookups`, { headers }),
      ]);

      const nextAssignments = Array.isArray(assignmentResponse.data) ? assignmentResponse.data : [];
      setAssignments(nextAssignments);
      setTeachers(Array.isArray(lookupResponse.data?.teachers) ? lookupResponse.data.teachers : []);
      setStudents(Array.isArray(lookupResponse.data?.students) ? lookupResponse.data.students : []);
      setCourses(Array.isArray(lookupResponse.data?.courses) ? lookupResponse.data.courses : []);

      if (!selectedAssignmentId && nextAssignments.length > 0) {
        setSelectedAssignmentId(nextAssignments[0].id);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load teaching assignments.');
    } finally {
      setLoading(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization, selectedAssignmentId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!assignments.length) {
      setSelectedAssignmentId(null);
      if (mode !== 'create') {
        setMode('view');
      }
      return;
    }

    if (selectedAssignmentId && assignments.some((item) => item.id === selectedAssignmentId)) {
      return;
    }

    setSelectedAssignmentId(assignments[0].id);
  }, [assignments, mode, selectedAssignmentId]);

  useEffect(() => {
    setTeacherFilter(requestedTeacherUserId);
    setStudentFilter(requestedStudentUserId);
  }, [requestedStudentUserId, requestedTeacherUserId]);

  const filteredRows = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    return assignments.filter((item) => {
      if (search) {
        const haystack = [item.teacherDisplayName, item.studentDisplayName, item.courseName, item.status, item.deliveryType, item.notes || ''].join(' ').toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (statusFilter && item.status !== statusFilter) {
        return false;
      }

      if (teacherFilter && String(item.teacherUserId) !== teacherFilter) {
        return false;
      }

      if (studentFilter && String(item.studentUserId) !== studentFilter) {
        return false;
      }

      return true;
    });
  }, [assignments, searchValue, statusFilter, teacherFilter, studentFilter]);

  useEffect(() => {
    if (mode !== 'view') {
      return;
    }

    if (!filteredRows.length) {
      setSelectedAssignmentId(null);
      return;
    }

    if (!selectedAssignmentId || !filteredRows.some((item) => item.id === selectedAssignmentId)) {
      setSelectedAssignmentId(filteredRows[0].id);
    }
  }, [filteredRows, mode, selectedAssignmentId]);

  const beginCreate = () => {
    setMode('create');
    setSelectedAssignmentId(null);
    setForm(emptyForm);
    setError(null);
    setMessage(null);
  };

  const beginEdit = (assignment: TeachingAssignment) => {
    setMode('edit');
    setSelectedAssignmentId(assignment.id);
    setForm(mapAssignmentToForm(assignment));
    setError(null);
    setMessage(null);
  };

  const beginView = (assignment: TeachingAssignment) => {
    setMode('view');
    setSelectedAssignmentId(assignment.id);
    setError(null);
    setMessage(null);
  };

  const setField = (field: keyof FormState, value: string | boolean) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const validateForm = (): string | null => {
    if (!form.teacherUserId) {
      return 'Teacher is required.';
    }

    if (!form.studentUserId) {
      return 'Student is required.';
    }

    if (!form.courseId) {
      return 'Subject / course is required.';
    }

    if (!form.startDate) {
      return 'Start date is required.';
    }

    if (form.endDate && form.endDate < form.startDate) {
      return 'End date cannot be before start date.';
    }

    return null;
  };

  const buildPayload = () => ({
    teacherUserId: Number(form.teacherUserId),
    studentUserId: Number(form.studentUserId),
    courseId: Number(form.courseId),
    deliveryType: form.deliveryType,
    status: form.status,
    isPrimary: form.isPrimary,
    startDate: new Date(`${form.startDate}T00:00:00`).toISOString(),
    endDate: form.endDate ? new Date(`${form.endDate}T00:00:00`).toISOString() : null,
    notes: form.notes.trim() || null,
  });

  const saveAssignment = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      const payload = buildPayload();

      if (mode === 'create') {
        await axios.post(`${API_BASE_URL}/teachingassignments`, payload, { headers });
        setMessage('Teaching assignment created successfully.');
      } else if (mode === 'edit' && selectedAssignmentId) {
        await axios.put(`${API_BASE_URL}/teachingassignments/${selectedAssignmentId}`, payload, { headers });
        setMessage('Teaching assignment updated successfully.');
      }

      setMode('view');
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save teaching assignment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs items={[
        { label: 'Administration', to: '/analytics' },
        { label: 'Teaching Assignments', isCurrent: true },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, color: '#173159' }}>Teaching Assignments</h2>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Manage teacher, student, and subject/course relationships for one-to-one and guided sessions.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="amd-btn amd-btn-primary" onClick={beginCreate}>Create Assignment</button>
          <button type="button" className="amd-btn amd-btn-ghost" onClick={() => { void loadData(); }}>Refresh</button>
        </div>
      </div>

      {!hasActiveOrganization && (
        <div style={{ borderRadius: 10, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', padding: '10px 12px', fontSize: '0.84rem' }}>
          No organization selected. Please switch organization first.
        </div>
      )}

      {(error || message) && (
        <div style={{ borderRadius: 10, border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`, background: error ? '#fff1f2' : '#eff6ff', color: error ? '#b91c1c' : '#1d4ed8', padding: '10px 12px', fontSize: '0.84rem' }}>
          {error || message}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input value={searchValue} onChange={(event) => setSearchValue(event.target.value)} placeholder="Search teacher, student, subject..." style={{ minWidth: 260, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }} />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={{ minWidth: 160, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}>
          <option value="">All statuses</option>
          <option value="Active">Active</option>
          <option value="Paused">Paused</option>
          <option value="Ended">Ended</option>
        </select>
        <select value={teacherFilter} onChange={(event) => setTeacherFilter(event.target.value)} style={{ minWidth: 200, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}>
          <option value="">All teachers</option>
          {teachers.map((item) => (
            <option key={item.userId} value={item.userId}>{item.displayName || item.email}</option>
          ))}
        </select>
        <select value={studentFilter} onChange={(event) => setStudentFilter(event.target.value)} style={{ minWidth: 200, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}>
          <option value="">All students</option>
          {students.map((item) => (
            <option key={item.userId} value={item.userId}>{item.displayName || item.email}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(520px, 58%) minmax(340px, 42%)', gap: 12 }}>
        <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #edf2fb', fontWeight: 700, color: '#173159' }}>Assignment List</div>
          {loading ? (
            <div style={{ padding: 14, color: '#475569' }}>Loading teaching assignments...</div>
          ) : filteredRows.length === 0 ? (
            <div style={{ padding: 14, color: '#64748b' }}>No teaching assignments found.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fbff', color: '#334155' }}>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Teacher</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Student</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Subject / Course</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #edf2fb' }}>
                      <td style={{ padding: '8px 10px' }}>{row.teacherDisplayName}</td>
                      <td style={{ padding: '8px 10px' }}>{row.studentDisplayName}</td>
                      <td style={{ padding: '8px 10px' }}>{row.courseName}</td>
                      <td style={{ padding: '8px 10px' }}>{row.status}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        <button type="button" className="amd-btn amd-btn-ghost" onClick={() => beginView(row)} style={{ marginRight: 6 }}>View</button>
                        <button type="button" className="amd-btn amd-btn-secondary" onClick={() => beginEdit(row)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 14 }}>
          <h3 style={{ marginTop: 0, marginBottom: 10, color: '#173159' }}>
            {mode === 'create' ? 'Create Teaching Assignment' : mode === 'edit' ? 'Edit Teaching Assignment' : 'Assignment Details'}
          </h3>

          {mode === 'view' && selectedAssignment ? (
            <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
              <div><strong>Teacher:</strong> {selectedAssignment.teacherDisplayName}</div>
              <div><strong>Student:</strong> {selectedAssignment.studentDisplayName}</div>
              <div><strong>Subject / Course:</strong> {selectedAssignment.courseName}</div>
              <div><strong>Delivery:</strong> {selectedAssignment.deliveryType}</div>
              <div><strong>Status:</strong> {selectedAssignment.status}</div>
              <div><strong>Primary:</strong> {selectedAssignment.isPrimary ? 'Yes' : 'No'}</div>
              <div><strong>Start:</strong> {formatDate(selectedAssignment.startDate)}</div>
              <div><strong>End:</strong> {formatDate(selectedAssignment.endDate)}</div>
              <div><strong>Notes:</strong> {selectedAssignment.notes || '—'}</div>
            </div>
          ) : mode === 'view' ? (
            <div style={{ color: '#64748b', fontSize: 14 }}>Select an assignment to view details, or create a new one.</div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#475569' }}>Teacher</span>
                <select value={form.teacherUserId} onChange={(event) => setField('teacherUserId', event.target.value)}>
                  <option value="">Select teacher</option>
                  {teachers.map((item) => (
                    <option key={item.userId} value={item.userId}>{item.displayName || item.email}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#475569' }}>Student</span>
                <select value={form.studentUserId} onChange={(event) => setField('studentUserId', event.target.value)}>
                  <option value="">Select student</option>
                  {students.map((item) => (
                    <option key={item.userId} value={item.userId}>{item.displayName || item.email}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#475569' }}>Subject / Course</span>
                <select value={form.courseId} onChange={(event) => setField('courseId', event.target.value)}>
                  <option value="">Select course</option>
                  {courses.map((item) => (
                    <option key={item.id} value={item.id}>{item.pathLabel}</option>
                  ))}
                </select>
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Delivery type</span>
                  <select value={form.deliveryType} onChange={(event) => setField('deliveryType', event.target.value)}>
                    <option value="OneToOne">One to One</option>
                    <option value="ClassroomSupport">Classroom Support</option>
                    <option value="Group">Group</option>
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Status</span>
                  <select value={form.status} onChange={(event) => setField('status', event.target.value)}>
                    <option value="Active">Active</option>
                    <option value="Paused">Paused</option>
                    <option value="Ended">Ended</option>
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Start date</span>
                  <input type="date" value={form.startDate} onChange={(event) => setField('startDate', event.target.value)} />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>End date</span>
                  <input type="date" value={form.endDate} onChange={(event) => setField('endDate', event.target.value)} />
                </label>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155' }}>
                <input type="checkbox" checked={form.isPrimary} onChange={(event) => setField('isPrimary', event.target.checked)} />
                Primary teacher relationship
              </label>

              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#475569' }}>Notes</span>
                <textarea value={form.notes} onChange={(event) => setField('notes', event.target.value)} rows={4} />
              </label>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="amd-btn amd-btn-ghost"
                  onClick={() => {
                    setMode('view');
                    if (selectedAssignment) {
                      setForm(mapAssignmentToForm(selectedAssignment));
                    } else {
                      setForm(emptyForm);
                    }
                  }}
                >
                  Cancel
                </button>
                <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void saveAssignment(); }} disabled={saving}>
                  {saving ? 'Saving...' : mode === 'create' ? 'Create Assignment' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageTeachingAssignments;
